import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@aqarmap/aqarmap-ui';
import { useAppDispatch } from '~/redux/hooks';
import useBreakpoint from '~/hooks/useBreakpoint';
import { setFooterDisplay } from '~/redux/features/uiState/uiStateSlice';
import { useFullRoutes, useQuery, useWallet } from '~/hooks';
import { useGetUserQuery } from '~/redux/api/userApiSlice';
import { useGetUserSegmentQuery } from '~/redux/api/aqarConnectApiSlice';
import { usePostPaymentPayMutation } from '~/redux/api/PaymentApiSlice';
import { SelectedPaymentType } from '~/types/paymentTypes';
import PurchasePaymentMethod from '~/components/PurchasePaymentMethod';
import PaymentSummaryWrapper from '~/components/PaymentSummaryWrapper';
import SegmentDetails from '~aqarConnect/pages/SegmentConnectCheckout/SegmentDetails';
import '~/cardsIcons.css';
import { RequestStatusEnum } from '~/constants/enums';
import {
  PaymentMethodEnum,
  PaymentOperationTypeEnum,
} from '~/constants/enums/paymentEnums';
import { UserCardFormHandle } from '~/components/UserCardForm/types';

export default function SegmentConnectCheckout() {
  const userCardFormRef = useRef<UserCardFormHandle>(null);
  const { AQAR_CONNECT_URL } = useFullRoutes();
  const dispatch = useAppDispatch();
  const { walletBalance } = useWallet({});
  const { id } = useParams();
  const { status, final } = useQuery();
  const [checkoutError, setCheckoutError] = useState<string>('');
  const { mobileScreen } = useBreakpoint();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const [selectedPayment, setSelectedPayment] = useState<
    SelectedPaymentType | undefined
  >(undefined);
  const [isPayfortLoading, setIsPayfortLoading] = useState<boolean>(false);
  const { isLoading: isUserLoading, isSuccess: isUserSuccess } =
    useGetUserQuery();
  const [postPaymentPay, { isLoading: isSegmentConnectLoading }] =
    usePostPaymentPayMutation();
  const {
    data: segment,
    isLoading: isSegmentLoading,
    isSuccess: isSegmentSuccess,
  } = useGetUserSegmentQuery({ userSegment: id, locale });

  const isPageLoading = isUserLoading || isSegmentLoading;
  const isPageSuccess = isUserSuccess && isSegmentSuccess;

  const amountDue = Number(
    Boolean(final) === true
      ? segment?.leads_bank_proposal_fees?.remaining_amount
      : segment?.leads_bank_proposal_fees?.down_payment_amount,
  );

  const payWithWallet = !!selectedPayment?.payWithWallet;
  const hasEnoughBalance = walletBalance >= amountDue;
  const isWalletBalanceInsufficient = payWithWallet && !hasEnoughBalance;
  const isSubmitDisabled =
    !selectedPayment ||
    isSegmentConnectLoading ||
    isPayfortLoading ||
    isWalletBalanceInsufficient;

  const handlePayWithSavedCard = async () => {
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.SAVED_CARD,
      operationType: PaymentOperationTypeEnum.PROPOSAL,
      cardId: selectedPayment?.id,
      segmentId: segment?.id,
    });
    const { data, error } = response;

    if (data?.status === 'success') {
      window.location.href = `${process.env.REACT_APP_BASE_URL}${AQAR_CONNECT_URL.ROOT}`;
    } else if (data?.status === '3ds_required' && data?.['3ds_url']) {
      window.location.href = data['3ds_url'];
    } else if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError(t((error.data as any).message));
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const handlePayWithWallet = async () => {
    if (isWalletBalanceInsufficient) return;
    const response = await postPaymentPay({
      paymentMethod: PaymentMethodEnum.WALLET,
      operationType: PaymentOperationTypeEnum.PROPOSAL,
      segmentId: segment?.id,
    });
    const { data, error } = response;

    if (data?.status === 'success') {
      window.location.href = `${process.env.REACT_APP_BASE_URL}${AQAR_CONNECT_URL.ROOT}`;
    } else if (error && 'data' in error && (error.data as any)?.message) {
      setCheckoutError(t((error.data as any).message));
    } else {
      setCheckoutError(t('somethingWrong'));
    }
  };

  const onCheckoutSubmit = () => {
    const isNewCard = selectedPayment?.isNew || false;
    const isValidUserCardForm = userCardFormRef?.current;
    const isValidSegment = id && Boolean(segment?.id);
    if (isValidSegment) {
      if (payWithWallet) {
        handlePayWithWallet();
      } else if (isNewCard && isValidUserCardForm) {
        setIsPayfortLoading(true);
        userCardFormRef.current.submitUserCard({
          operationType: PaymentOperationTypeEnum.PROPOSAL,
          segmentId: segment?.id,
          returnTo: `${process.env.REACT_APP_BASE_URL}${AQAR_CONNECT_URL.CHECKOUT(
            id,
          )}`,
        });
      } else {
        handlePayWithSavedCard();
      }
    }
  };

  const handleStatusFallback = () => {
    switch (status) {
      case RequestStatusEnum.FAILED:
        setCheckoutError(t('somethingWrong'));
        setSelectedPayment({
          isNew: true,
          id: 0,
          payWithWallet: false,
        });
        break;
      case RequestStatusEnum.SUCCESS:
        window.location.href = `${process.env.REACT_APP_BASE_URL}${AQAR_CONNECT_URL.ROOT}`;
        break;
    }
  };

  useEffect(() => {
    if (mobileScreen) {
      dispatch(setFooterDisplay('hide'));
    }
  }, [mobileScreen]);

  useEffect(() => {
    handleStatusFallback();
  }, [status]);

  useEffect(() => {
    document.title = `${t('checkout')}|${t('aqarmap')}`;
  }, []);

  return (
    <div className="layout-boundary pt-4x pb-[40px] md:pb-[80px]">
      <div className="layout-boundary__inner flex flex-col-reverse md:flex-row gap-4x justify-between px-2x md:px-0">
        <PurchasePaymentMethod
          title={t('paymentDetails')}
          enableWallet={true}
          setSelectedPayment={setSelectedPayment}
          selectedPayment={selectedPayment}
          error={checkoutError}
          newCardProps={{
            onFailure: () => setCheckoutError(t('somethingWrong')),
            ref: userCardFormRef,
          }}
        />
        <PaymentSummaryWrapper
          isLoading={isPageLoading || !isPageSuccess}
          totalFeesLabel={t('totalFees')}
          submitBtnProps={{
            onClick: onCheckoutSubmit,
            isLoading: isSegmentConnectLoading,
            disabled: isSubmitDisabled,
          }}
          fees={
            <Trans
              i18nKey="priceValue"
              t={t}
              values={{
                value:
                  Boolean(final) === true
                    ? segment?.leads_bank_proposal_fees?.remaining_amount
                    : segment?.leads_bank_proposal_fees?.down_payment_amount,
              }}
              components={{
                tag: <span>placeholder</span>,
              }}
            />
          }
          summary={
            <SegmentDetails segment={segment} isFinalStep={Boolean(final)} />
          }
          title={t('planSummary')}
        />
        <div className="flex z-[40] md:hidden justify-between items-center  fixed bottom-0 left-0 w-full pt-2x pb-3x bg-white px-2x  border-t border-gray">
          <Button
            size="medium"
            variant="primary"
            className="w-full md:hidden"
            onClick={onCheckoutSubmit}
            isLoading={isSegmentConnectLoading || isPayfortLoading}
            disabled={isSubmitDisabled}
          >
            {t('pay')}
          </Button>
        </div>
      </div>
    </div>
  );
}
